import React, { useState, useEffect } from "react";
import {
  Loader2, Sparkles, Users, Film, Wand2, Music, Clapperboard, Zap,
  FlaskConical, Wrench, CheckCircle2, AlertTriangle, Rocket, Package, Download, Radio, ExternalLink,
  Play, Volume2, Boxes, Gamepad2, Cpu, Hammer, Terminal, Maximize2, X, HardDrive, ScanFace, PersonStanding, MessageCircle,
  Store, History, Trophy, Crown, Lock, Clock, Plus, Trash2,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import VisualQAScan from "@/components/studiopro/VisualQAScan";
import NpcChatModal from "@/components/studiopro/NpcChatModal";

/* Shared run helper — POST a stage endpoint, toast, reload. Returns [busy, run]. */
export function useStageRun(job, reload) {
  const [busy, setBusy] = useState("");
  const run = async (kind, path, body, msg) => {
    if (job || busy) return;
    setBusy(kind);
    try {
      await api.post(path, body || {});
      toast.success(msg);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start that.");
    } finally { setBusy(""); }
  };
  return [busy, run];
}

const ROLE_STYLE = {
  player: "border-cyan-500/40 text-cyan-400", ally: "border-emerald-500/40 text-emerald-400",
  enemy: "border-rose-500/40 text-rose-400", boss: "border-amber-500/40 text-amber-400",
  npc: "border-violet-500/40 text-violet-400",
};
const ROLE_EMOJI = { player: "🎮", ally: "🤝", enemy: "💀", boss: "👑", npc: "💬" };

function SubTabs({ subs, sub, setSub }) {
  return (
    <div className="flex gap-1.5 mb-4 flex-wrap">
      {subs.map((s) => {
        const Icon = s.icon; const active = sub === s.key;
        return (
          <button key={s.key} onClick={() => setSub(s.key)} data-testid={`gv2-subtab-${s.key}`}
            className={`flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm border transition-colors ${active ? "border-violet-500 bg-violet-500/10 text-violet-300 font-semibold" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-violet-500/40"}`}>
            <Icon className="w-3.5 h-3.5" /> {s.label}
          </button>
        );
      })}
    </div>
  );
}

const Empty = ({ children }) => (
  <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">{children}</div>
);

/* Per-NPC merchant pricing — markup or loyal-customer discount; commits on blur. */
function MerchantPricing({ pricing, onCommit, disabled }) {
  const [markup, setMarkup] = useState(String(pricing?.markup ?? 0));
  const [loyal, setLoyal] = useState(String(pricing?.loyal ?? 0));
  useEffect(() => { setMarkup(String(pricing?.markup ?? 0)); setLoyal(String(pricing?.loyal ?? 0)); }, [pricing?.markup, pricing?.loyal]);
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(Number(v) || 0)));
  const commit = () => {
    const m = clamp(markup, -90, 300), l = clamp(loyal, 0, 90);
    if (m !== (pricing?.markup ?? 0) || l !== (pricing?.loyal ?? 0)) onCommit({ markup: m, loyal: l });
  };
  const box = "w-12 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-md px-1.5 py-0.5 text-[10px] text-center tabular-nums focus:border-amber-500/60 outline-none";
  return (
    <span className="inline-flex items-center gap-2 ml-auto shrink-0" data-testid="gv2-merchant-pricing">
      <label className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]" title="Markup on list price (negative = cheaper)">
        Markup
        <input type="number" value={markup} disabled={disabled} data-testid="gv2-merchant-markup"
          onChange={(e) => setMarkup(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className={box} />%
      </label>
      <label className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]" title="Extra discount for loyal (Trusted+) customers">
        Loyal −
        <input type="number" value={loyal} disabled={disabled} data-testid="gv2-merchant-loyal"
          onChange={(e) => setLoyal(e.target.value)} onBlur={commit}
          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} className={box} />%
      </label>
    </span>
  );
}

const PERK_TIER_META = [
  { k: "Known", icon: "✨", label: "Known" },
  { k: "Trusted", icon: "⭐", label: "Trusted" },
  { k: "Honored", icon: "🏅", label: "Honored" },
  { k: "Legend", icon: "👑", label: "Legend" },
];

/* Reputation perks — per-tier discount + exclusive items/quests only high-standing players unlock. */
function ReputationPerks({ perks, shopCatalog, questCatalog, onChange, disabled }) {
  const p = perks || { enabled: false, tiers: {} };
  const tiers = p.tiers || {};
  const patchTier = (tier, patch) => {
    const t = tiers[tier] || { discount: 0, items: [], quests: [] };
    onChange({ ...p, tiers: { ...tiers, [tier]: { ...t, ...patch } } });
  };
  const toggleIn = (arr, v) => (arr || []).includes(v) ? (arr || []).filter((x) => x !== v) : [...(arr || []), v];
  return (
    <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]" data-testid="gv2-rep-perks">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1">
          <Sparkles className="w-3.5 h-3.5" /> Reputation perks — reward loyal players at higher standing
        </div>
        <button disabled={disabled} data-testid="gv2-rep-perks-toggle"
          onClick={() => onChange({ ...p, enabled: !p.enabled })}
          className={`rounded-full px-3 py-1 text-[10px] font-bold border transition-colors ${p.enabled ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-amber-500/40"}`}>
          {p.enabled ? "On" : "Off"}
        </button>
      </div>
      {p.enabled && (
        <div className="space-y-2.5">
          {PERK_TIER_META.map((meta) => {
            const t = tiers[meta.k] || { discount: 0, items: [], quests: [] };
            return (
              <div key={meta.k} className="rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`gv2-rep-tier-${meta.k}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] font-bold w-24 shrink-0">{meta.icon} {meta.label}+</span>
                  <label className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                    Shop discount
                    <input type="number" value={t.discount ?? 0} disabled={disabled}
                      data-testid={`gv2-rep-${meta.k}-discount`}
                      onChange={(e) => patchTier(meta.k, { discount: Math.max(0, Math.min(90, Math.round(Number(e.target.value) || 0))) })}
                      className="w-12 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-md px-1.5 py-0.5 text-[10px] text-center tabular-nums focus:border-amber-500/60 outline-none" />%
                  </label>
                </div>
                {shopCatalog.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] w-24 shrink-0">Unlock items</span>
                    {shopCatalog.map((it) => {
                      const on = (t.items || []).includes(it.id);
                      return (
                        <button key={it.id} disabled={disabled} data-testid={`gv2-rep-${meta.k}-item-${it.id}`}
                          onClick={() => patchTier(meta.k, { items: toggleIn(t.items, it.id) })}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors ${on ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-amber-500/40"}`}>
                          {it.emoji} {it.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                {questCatalog.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    <span className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] w-24 shrink-0">Unlock quests</span>
                    {questCatalog.map((q) => {
                      const on = (t.quests || []).includes(q);
                      return (
                        <button key={q} disabled={disabled} data-testid={`gv2-rep-${meta.k}-quest-${q}`}
                          onClick={() => patchTier(meta.k, { quests: toggleIn(t.quests, q) })}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors ${on ? "border-violet-500 bg-violet-500/15 text-violet-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-violet-500/40"}`}>
                          📜 {q}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="text-[10px] text-[hsl(var(--muted-foreground))] italic">
            Perks stack as players climb. Discounts always apply in NPC shops; unlocked items appear in a merchant's stock and exclusive quests are only offered once earned.
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════ Phase 3 — Characters & Animation ══════════════ */

/* Playable NPCs — publish the brained cast into the live game build so players can walk up & chat. */
function LiveNpcCard({ projectId, npcReady, disabled }) {
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/gv2/projects/${projectId}/live-npc`)
    .then(({ data }) => setLive(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, npcReady]);

  const cur = live?.live || {};
  const brained = live?.brained || [];
  const shopCatalog = live?.shop_catalog || [];
  const questCatalog = live?.quest_catalog || [];
  const save = async (patch) => {
    if (saving || disabled) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/gv2/projects/${projectId}/live-npc`, {
        party_max: patch.party_max ?? cur.partyMax ?? 2,
        voice_default: patch.voice_default ?? (cur.voiceDefault !== false),
        quest_handoff: patch.quest_handoff ?? (cur.questHandoff || "off"),
        npc_shops: patch.npc_shops ?? !!cur.npcShops,
        cross_game: patch.cross_game ?? (cur.crossGame !== false),
        tier_perks: patch.tier_perks ?? cur.tierPerks ?? null,
      });
      if (!patch._silent) toast.success(`Live in-game — ${data.ai_npc_count} character(s) can talk while playing.`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update."); }
    finally { setSaving(false); }
  };
  const toggleNpcItem = async (nodeId, itemId, selected) => {
    if (saving || disabled) return;
    const npc = brained.find((n) => n.node_id === nodeId);
    const curItems = npc?.shop_items || [];
    const next = selected ? curItems.filter((i) => i !== itemId) : [...curItems, itemId];
    try { await api.put(`/gv2/projects/${projectId}/npc/${nodeId}/shop`, { item_ids: next }); await load(); }
    catch (e) { toast.error("Couldn't update merchant stock."); }
  };
  const savePricing = async (nodeId, pricing) => {
    if (disabled) return;
    try { await api.put(`/gv2/projects/${projectId}/npc/${nodeId}/shop`, pricing); await load(); }
    catch (e) { toast.error("Couldn't update merchant pricing."); }
  };
  const saveTierLines = async (nodeId, lines) => {
    if (disabled) return;
    try { await api.put(`/gv2/projects/${projectId}/npc/${nodeId}/tier-lines`, { lines }); await load(); }
    catch (e) { toast.error("Couldn't save secret lines."); }
  };

  if (loading || !live?.has_game) return null;
  const enabled = !!cur.enabled;
  const partyMax = cur.partyMax || 2;
  const voiceOn = cur.voiceDefault !== false;
  const questMode = cur.questHandoff || "off";
  const shopsOn = !!cur.npcShops;
  const crossOn = cur.crossGame !== false;
  const count = cur.ai_npc_count || 0;
  const QUEST_OPTS = [
    { k: "off", label: "Off" },
    { k: "items", label: "Real rewards" },
    { k: "open", label: "Can invent" },
  ];

  return (
    <div className="rounded-2xl border border-violet-500/25 bg-violet-500/[0.04] p-3 mb-4" data-testid="gv2-live-npc-card">
      <div className="flex items-center gap-3">
        <Gamepad2 className="w-5 h-5 text-violet-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold flex items-center gap-2">
            NPCs In-Game
            {enabled && <Badge variant="outline" className="text-[9px] gap-1 border-emerald-500/40 text-emerald-400" data-testid="gv2-live-npc-status">● LIVE · {count}</Badge>}
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
            {enabled
              ? "Walk up to a character in the playable build and press E to chat — live, voiced, with memory."
              : "Put your talkable cast into the playable build so players can chat with them mid-game."}
          </div>
        </div>
        <Button size="sm" disabled={disabled || saving} data-testid="gv2-live-npc-enable-btn"
          onClick={() => save({})}
          className="rounded-full gap-1.5 bg-gradient-to-r from-violet-600 to-cyan-600 shrink-0">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gamepad2 className="w-4 h-4" />}
          {enabled ? "Re-sync" : "Put NPCs in game"}
        </Button>
      </div>

      {enabled && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] flex flex-wrap items-center gap-x-5 gap-y-3">
          <div className="flex items-center gap-2" data-testid="gv2-live-npc-party">
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Party chat</span>
            {[1, 2, 3, 4].map((n) => (
              <button key={n} disabled={disabled || saving} data-testid={`gv2-party-max-${n}`}
                onClick={() => save({ party_max: n })}
                className={`w-7 h-7 rounded-lg text-xs font-bold border transition-colors ${partyMax === n ? "border-violet-500 bg-violet-500/15 text-violet-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-violet-500/40"}`}>
                {n}
              </button>
            ))}
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">NPC{partyMax === 1 ? "" : "s"} at once</span>
          </div>
          <button disabled={disabled || saving} data-testid="gv2-live-npc-voice-toggle"
            onClick={() => save({ voice_default: !voiceOn })}
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-colors ${voiceOn ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
            <Volume2 className="w-3.5 h-3.5" /> Voice {voiceOn ? "on" : "off"} by default
          </button>
          <div className="flex items-center gap-2" data-testid="gv2-live-npc-quest">
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] flex items-center gap-1" title="Let NPCs hand the player items/coins/quests mid-chat">
              <Package className="w-3.5 h-3.5" /> Quest handoff
            </span>
            {QUEST_OPTS.map((o) => (
              <button key={o.k} disabled={disabled || saving} data-testid={`gv2-quest-handoff-${o.k}`}
                onClick={() => save({ quest_handoff: o.k })}
                className={`rounded-full px-2.5 py-1 text-[10px] font-bold border transition-colors ${questMode === o.k ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-amber-500/40"}`}>
                {o.label}
              </button>
            ))}
          </div>
          <button disabled={disabled || saving} data-testid="gv2-live-npc-shops-toggle"
            onClick={() => save({ npc_shops: !shopsOn })}
            title="Let a merchant NPC open the game's shop mid-chat"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-colors ${shopsOn ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
            <Store className="w-3.5 h-3.5" /> NPC shops {shopsOn ? "on" : "off"}
          </button>
          <button disabled={disabled || saving} data-testid="gv2-live-npc-cross-toggle"
            onClick={() => save({ cross_game: !crossOn })}
            title="A returning player is recognised across all of your games (same device)"
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold border transition-colors ${crossOn ? "border-violet-500/50 bg-violet-500/10 text-violet-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]"}`}>
            <History className="w-3.5 h-3.5" /> Remember across my games {crossOn ? "on" : "off"}
          </button>
        </div>
      )}

      {enabled && shopsOn && shopCatalog.length > 0 && brained.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]" data-testid="gv2-merchant-stock">
          <div className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1">
            <Store className="w-3.5 h-3.5" /> Merchant stock — pick what each NPC sells (none = whole shop)
          </div>
          <div className="space-y-2.5">
            {brained.map((npc) => {
              const sel = npc.shop_items || [];
              return (
                <div key={npc.node_id} className="flex flex-wrap items-center gap-1.5" data-testid={`gv2-merchant-${npc.node_id}`}>
                  <span className="text-[11px] font-bold text-[hsl(var(--foreground))] w-28 shrink-0 truncate">{npc.name}</span>
                  {shopCatalog.map((it) => {
                    const on = sel.includes(it.id);
                    return (
                      <button key={it.id} disabled={disabled || saving}
                        data-testid={`gv2-merchant-${npc.node_id}-${it.id}`}
                        onClick={() => toggleNpcItem(npc.node_id, it.id, on)}
                        className={`rounded-full px-2.5 py-1 text-[10px] font-semibold border transition-colors ${on ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-amber-500/40"}`}>
                        {it.emoji} {it.name}
                      </button>
                    );
                  })}
                  {sel.length === 0 && <span className="text-[10px] text-[hsl(var(--muted-foreground))] italic">sells everything</span>}
                  <MerchantPricing pricing={npc.pricing} disabled={disabled} onCommit={(pr) => savePricing(npc.node_id, pr)} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {enabled && (
        <ReputationPerks perks={cur.tierPerks} shopCatalog={shopCatalog} questCatalog={questCatalog}
          disabled={disabled || saving} onChange={(next) => save({ tier_perks: next, _silent: true })} />
      )}

      {enabled && brained.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]" data-testid="gv2-tier-dialogue">
          <div className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1">
            <Lock className="w-3.5 h-3.5" /> Tier-gated dialogue — secret lines an NPC reveals only to high-standing players
          </div>
          <div className="space-y-2.5">
            {brained.map((npc) => (
              <NpcTierLines key={npc.node_id} npc={npc} disabled={disabled}
                onSave={(lines) => saveTierLines(npc.node_id, lines)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const TIER_PILL = {
  Legend: "text-amber-300 border-amber-500/50 bg-amber-500/10",
  Honored: "text-fuchsia-300 border-fuchsia-500/50 bg-fuchsia-500/10",
  Trusted: "text-cyan-300 border-cyan-500/50 bg-cyan-500/10",
  Known: "text-violet-300 border-violet-500/50 bg-violet-500/10",
  Stranger: "text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
};
const TIER_ICON = { Legend: "👑", Honored: "🏅", Trusted: "⭐", Known: "✨", Stranger: "🌱" };

/* Per-NPC secret lines unlocked at a standing tier. */
function NpcTierLines({ npc, disabled, onSave }) {
  const [lines, setLines] = useState(npc.tier_lines || []);
  useEffect(() => { setLines(npc.tier_lines || []); /* eslint-disable-next-line */ }, [npc.node_id]);
  const commit = (next) => { setLines(next); onSave(next.filter((l) => (l.text || "").trim())); };
  const setTier = (i, tier) => commit(lines.map((l, j) => (j === i ? { ...l, tier } : l)));
  const setText = (i, text) => setLines(lines.map((l, j) => (j === i ? { ...l, text } : l)));
  const add = () => setLines([...lines, { tier: "Trusted", text: "" }]);
  const remove = (i) => commit(lines.filter((_, j) => j !== i));
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] p-2.5" data-testid={`gv2-tierlines-${npc.node_id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] font-bold truncate">{npc.name}</span>
        <button disabled={disabled} data-testid={`gv2-tierline-add-${npc.node_id}`} onClick={add}
          className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold border border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-cyan-500/40">
          <Plus className="w-3 h-3" /> secret line
        </button>
      </div>
      {lines.length === 0 && <div className="text-[10px] text-[hsl(var(--muted-foreground))] italic">no secret lines — talks the same to everyone</div>}
      <div className="space-y-1.5">
        {lines.map((l, i) => (
          <div key={i} className="flex items-center gap-1.5" data-testid={`gv2-tierline-${npc.node_id}-${i}`}>
            <select value={l.tier} disabled={disabled} data-testid={`gv2-tierline-tier-${npc.node_id}-${i}`}
              onChange={(e) => setTier(i, e.target.value)}
              className="bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-md px-1.5 py-1 text-[10px] font-semibold">
              <option value="Known">✨ Known+</option>
              <option value="Trusted">⭐ Trusted+</option>
              <option value="Honored">🏅 Honored+</option>
              <option value="Legend">👑 Legend+</option>
            </select>
            <input value={l.text} disabled={disabled} data-testid={`gv2-tierline-text-${npc.node_id}-${i}`}
              onChange={(e) => setText(i, e.target.value)} onBlur={() => commit(lines)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              placeholder="A secret this NPC only tells trusted players…" maxLength={240}
              className="flex-1 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-md px-2 py-1 text-[11px] focus:border-cyan-500/60 outline-none" />
            <button disabled={disabled} data-testid={`gv2-tierline-del-${npc.node_id}-${i}`} onClick={() => remove(i)}
              className="text-[hsl(var(--muted-foreground))] hover:text-rose-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Standing Leaderboard + Season control — a creator's most-loyal players across ALL their games. */
function StandingsCard({ projectId, disabled }) {
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null);
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("auto");
  const [days, setDays] = useState(30);

  const load = () => Promise.all([
    api.get(`/gv2/standings`).then((r) => r.data).catch(() => null),
    api.get(`/gv2/season`).then((r) => r.data).catch(() => null),
    api.get(`/gv2/season/recap`).then((r) => r.data).catch(() => null),
  ]).then(([s, se, rc]) => { setData(s); setSeason(se?.season || null); setRecap(rc?.recap || null); }).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const start = async () => {
    setBusy(true);
    try { await api.post(`/gv2/season/start`, { mode, length_days: Number(days) || 30 }); toast.success("Reputation season started."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the season."); }
    finally { setBusy(false); }
  };
  const end = async () => {
    if (!window.confirm("End the season now? Every player's standing resets to 0, the top players earn permanent keepsake badges, and a shareable recap card is generated.")) return;
    setBusy(true);
    try { const { data: r } = await api.post(`/gv2/season/end`); toast.success(`Season ${r.season_no} ended — ${r.awarded} keepsake badge(s) awarded + recap card ready.`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't end the season."); }
    finally { setBusy(false); }
  };
  const recapUrl = recap ? `${process.env.REACT_APP_BACKEND_URL}/api/gv2/public/recap/${recap.id}` : "";
  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(recapUrl); toast.success("Share link copied!"); return; }
      throw new Error("no clipboard");
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = recapUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      let ok = false;
      try { ok = document.execCommand("copy"); } catch (_) {}
      document.body.removeChild(ta);
      ok ? toast.success("Share link copied!") : window.prompt("Copy this share link:", recapUrl);
    }
  };
  const downloadPng = () => {
    if (!recap) return;
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas"); c.width = 1200; c.height = 630;
      const ctx = c.getContext("2d"); ctx.fillStyle = "#0b1026"; ctx.fillRect(0, 0, 1200, 630);
      ctx.drawImage(img, 0, 0, 1200, 630);
      c.toBlob((blob) => {
        if (!blob) { toast.error("Export failed — use Copy link instead."); return; }
        const u = URL.createObjectURL(blob); const a = document.createElement("a");
        a.href = u; a.download = `season-${recap.season_no}-champions.png`; a.click(); URL.revokeObjectURL(u);
      }, "image/png");
    };
    img.onerror = () => toast.error("Export failed — use Copy link instead.");
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(recap.svg);
  };

  if (loading) return null;
  const rows = data?.standings || [];
  const total = data?.total_players || 0;
  let resetsIn = null;
  if (season?.ends_at) {
    const ms = new Date(season.ends_at).getTime() - Date.now();
    resetsIn = ms > 0 ? Math.ceil(ms / 86400000) : 0;
  }

  return (
    <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.04] p-3 mb-4" data-testid="gv2-standings-card">
      <div className="flex items-center gap-3">
        <Trophy className="w-5 h-5 text-amber-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold flex items-center gap-2">
            Standing Leaderboard
            <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))]">· your most-loyal players, all games</span>
          </div>
          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
            {season
              ? <>Season {season.season_no} · {season.mode === "auto" ? `auto${resetsIn !== null ? ` · resets in ${resetsIn}d` : ""}` : "manual"} · {total} player{total === 1 ? "" : "s"} tracked</>
              : <>No season running — standings accrue, but start a season to award keepsake badges on reset.</>}
          </div>
        </div>
        {season ? (
          <Button size="sm" variant="outline" disabled={disabled || busy} data-testid="gv2-season-end-btn" onClick={end}
            className="rounded-full gap-1.5 border-rose-500/40 text-rose-400 shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} End season
          </Button>
        ) : null}
      </div>

      {!season && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] flex flex-wrap items-center gap-2" data-testid="gv2-season-start">
          <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">Start a season:</span>
          {[{ k: "auto", label: "Auto (resets on schedule)" }, { k: "manual", label: "Manual (you control)" }].map((o) => (
            <button key={o.k} disabled={disabled || busy} data-testid={`gv2-season-mode-${o.k}`} onClick={() => setMode(o.k)}
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold border transition-colors ${mode === o.k ? "border-amber-500 bg-amber-500/15 text-amber-300" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-amber-500/40"}`}>
              {o.label}
            </button>
          ))}
          {mode === "auto" && (
            <label className="inline-flex items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
              every
              <input type="number" value={days} disabled={disabled || busy} data-testid="gv2-season-days"
                onChange={(e) => setDays(e.target.value)} min={1} max={365}
                className="w-14 bg-[hsl(var(--background))] border border-[hsl(var(--border))] rounded-md px-1.5 py-0.5 text-[10px] text-center tabular-nums focus:border-amber-500/60 outline-none" /> days
            </label>
          )}
          <Button size="sm" disabled={disabled || busy} data-testid="gv2-season-start-btn" onClick={start}
            className="rounded-full gap-1.5 bg-gradient-to-r from-amber-500 to-orange-600 shrink-0">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Start
          </Button>
        </div>
      )}

      {recap && (
        <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]" data-testid="gv2-recap">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">🏆 Season {recap.season_no} recap card — share your champions</span>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" disabled={disabled} data-testid="gv2-recap-copy" onClick={copyLink}
                className="rounded-full h-7 gap-1 text-[10px] px-2.5"><ExternalLink className="w-3 h-3" /> Copy link</Button>
              <Button size="sm" disabled={disabled} data-testid="gv2-recap-download" onClick={downloadPng}
                className="rounded-full h-7 gap-1 text-[10px] px-2.5 bg-gradient-to-r from-amber-500 to-orange-600"><Download className="w-3 h-3" /> PNG</Button>
            </div>
          </div>
          <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(recap.svg)}`} alt={`Season ${recap.season_no} champions`}
            data-testid="gv2-recap-img" className="w-full rounded-xl border border-[hsl(var(--border))]" />
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
        {rows.length === 0 ? (
          <div className="text-[11px] text-[hsl(var(--muted-foreground))] italic" data-testid="gv2-standings-empty">
            No ranked players yet — once people chat with your NPCs, your top patrons appear here.
          </div>
        ) : (
          <div className="space-y-1" data-testid="gv2-standings-list">
            {rows.map((r) => (
              <div key={r.rank} className="flex items-center gap-2 text-[11px] py-1" data-testid={`gv2-standing-row-${r.rank}`}>
                <span className="w-6 text-center font-black text-[hsl(var(--muted-foreground))]">{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : `#${r.rank}`}</span>
                <span className="flex-1 min-w-0 truncate font-semibold">
                  {r.label}{!r.account && <span className="text-[9px] text-[hsl(var(--muted-foreground))] ml-1">guest</span>}
                  {r.title && <span className="text-[9px] text-amber-300 ml-1.5 font-bold" data-testid={`gv2-standing-title-${r.rank}`}>· {r.title}</span>}
                </span>
                {r.badges > 0 && <span className="text-[9px] text-amber-400" title="keepsake badges">🎖️{r.badges}</span>}
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold border ${TIER_PILL[r.tier] || ""}`}>{TIER_ICON[r.tier]} {r.tier}</span>
                <span className="w-12 text-right tabular-nums text-[hsl(var(--muted-foreground))]">{r.rep}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CharactersView({ projectId, nodes, job, reload }) {
  const [sub, setSub] = useState("cast");
  const [charBrief, setCharBrief] = useState("");
  const [animBrief, setAnimBrief] = useState("");
  const [mocapPrompt, setMocapPrompt] = useState("");
  const [busy, run] = useStageRun(job, reload);
  const disabled = !!job || !!busy;

  const cast = nodes.filter((n) => n.type === "CHARACTER");
  const motion = nodes.find((n) => n.type === "MOTION");
  const mocapLib = nodes.find((n) => n.type === "ANIM" && n.parent === motion?.id && n.spec?.category === "mocap");
  const clips = nodes.filter((n) => n.type === "ANIM" && n.parent === motion?.id && n.spec?.category !== "mocap");
  const mocapClips = mocapLib?.spec?.clips || [];
  const humanoids = cast.filter((c) => (c.spec?.rig || "humanoid") === "humanoid");
  const mhCount = cast.filter((c) => c.spec?.metahuman_ready).length;
  const npcReady = cast.filter((c) => c.spec?.npc_ready).length;
  const [chatNode, setChatNode] = useState(null);

  return (
    <div className="max-w-3xl" data-testid="gv2-characters-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Phase 3 — the AI workforce designs your cast and animation system. High-fidelity humans (MetaHuman) and AI
        motion-capture (DeepMotion) are premium providers; the Web Runtime renders procedural & Meshy characters today.
      </p>
      <SubTabs sub={sub} setSub={setSub} subs={[
        { key: "cast", label: "Cast", icon: Users },
        { key: "animation", label: "Animation & Motion", icon: Film },
      ]} />

      {sub === "cast" && (
        <div data-testid="gv2-cast-panel">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Users className="w-4 h-4 text-cyan-400" /> Character Cast</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{cast.length} character{cast.length === 1 ? "" : "s"} designed</div>
            </div>
            <Button size="sm" disabled={disabled} data-testid="gv2-generate-characters-btn"
              onClick={() => run("char", `/gv2/projects/${projectId}/characters`, { brief: charBrief }, "Character Artist is designing your cast…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
              {busy === "char" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {cast.length ? "Add more" : "Design cast"}
            </Button>
          </div>
          <input value={charBrief} onChange={(e) => setCharBrief(e.target.value)}
            placeholder="Optional brief — e.g. a grizzled captain, a scout drone companion…"
            data-testid="gv2-char-brief"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
          {humanoids.length > 0 && (
            <div className="rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/[0.04] p-3 mb-4 flex items-center gap-3" data-testid="gv2-metahuman-bar">
              <ScanFace className="w-5 h-5 text-fuchsia-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold">MetaHuman Cast <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))]">· cinematic AAA humans</span></div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                  {mhCount > 0 ? `${mhCount}/${humanoids.length} humanoid(s) upgraded — RigLogic face, groom hair & Chaos cloth.`
                              : `Upgrade your ${humanoids.length} humanoid character(s) to high-fidelity MetaHumans for the Unreal build.`}
                </div>
              </div>
              <Button size="sm" disabled={disabled} data-testid="gv2-metahuman-btn"
                onClick={() => run("metahuman", `/gv2/projects/${projectId}/metahuman`, {}, "MetaHuman Director is upgrading your humanoid cast…")}
                className="rounded-full gap-1.5 bg-gradient-to-r from-fuchsia-600 to-violet-600 shrink-0">
                {busy === "metahuman" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanFace className="w-4 h-4" />}
                {mhCount > 0 ? "Re-generate" : "Generate MetaHumans"}
              </Button>
            </div>
          )}
          {cast.length > 0 && (
            <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/[0.04] p-3 mb-4 flex items-center gap-3" data-testid="gv2-npc-brains-bar">
              <MessageCircle className="w-5 h-5 text-cyan-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold">NPC Brains <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))]">· living, talkable characters</span></div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                  {npcReady > 0 ? `${npcReady}/${cast.length} character(s) can hold real conversations — tap Talk on a card.`
                              : `Give your ${cast.length} character(s) a persona, goals & memory so players can talk to them in-game.`}
                </div>
              </div>
              <Button size="sm" disabled={disabled} data-testid="gv2-npc-brains-btn"
                onClick={() => run("npc_brains", `/gv2/projects/${projectId}/npc-brains`, {}, "NPC Director is giving your cast living brains…")}
                className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600 shrink-0">
                {busy === "npc_brains" ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                {npcReady > 0 ? "Re-generate" : "Give NPCs brains"}
              </Button>
            </div>
          )}
          {npcReady > 0 && <LiveNpcCard projectId={projectId} npcReady={npcReady} disabled={disabled} />}
          {npcReady > 0 && <StandingsCard projectId={projectId} disabled={disabled} />}
          {cast.length === 0 ? (
            <Empty>No characters yet. The Character Artist will design the player, allies, enemies, bosses and NPCs — each with a distinct silhouette, personality and abilities.</Empty>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {cast.map((c) => (
                <div key={c.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`gv2-char-card-${c.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{ROLE_EMOJI[c.spec?.role] || "🧍"}</span>
                    <span className="font-bold truncate flex-1">{c.title}</span>
                    {c.spec?.metahuman_ready && (
                      <Badge variant="outline" className="text-[9px] border-fuchsia-500/50 text-fuchsia-400 gap-0.5" data-testid={`gv2-char-mh-${c.id}`}>
                        <ScanFace className="w-2.5 h-2.5" /> MetaHuman
                      </Badge>
                    )}
                    {c.spec?.npc_ready && (
                      <Badge variant="outline" className="text-[9px] border-cyan-500/50 text-cyan-400 gap-0.5" data-testid={`gv2-char-npc-${c.id}`}>
                        <MessageCircle className="w-2.5 h-2.5" /> NPC
                      </Badge>
                    )}
                    <Badge variant="outline" className={`text-[10px] capitalize ${ROLE_STYLE[c.spec?.role] || ""}`}>{c.spec?.role || "npc"}</Badge>
                  </div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1 capitalize">{c.spec?.rig || "humanoid"} rig</div>
                  {c.spec?.appearance && <p className="text-xs mb-2">{c.spec.appearance}</p>}
                  {c.spec?.personality && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mb-1">🎭 {c.spec.personality}</div>}
                  {c.spec?.metahuman && (
                    <div className="rounded-lg border border-fuchsia-500/20 bg-fuchsia-500/[0.04] px-2 py-1.5 mb-2 text-[10px] text-[hsl(var(--muted-foreground))]" data-testid={`gv2-char-mh-spec-${c.id}`}>
                      <span className="text-fuchsia-400 font-semibold">MetaHuman:</span> {c.spec.metahuman.preset}
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">RigLogic face</span>
                        <span className="bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">Groom hair</span>
                        {c.spec.metahuman.cloth_sim && <span className="bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">Chaos cloth</span>}
                        <span className="bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">{(c.spec.metahuman.lods || []).length} LODs</span>
                      </div>
                    </div>
                  )}
                  {c.spec?.abilities?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {c.spec.abilities.slice(0, 4).map((a, i) => <span key={i} className="text-[10px] bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">{String(a)}</span>)}
                    </div>
                  )}
                  {c.spec?.npc_ready && (
                    <Button size="sm" variant="outline" onClick={() => setChatNode({ id: c.id, title: c.title })}
                      data-testid={`gv2-char-talk-${c.id}`} className="rounded-full h-7 px-3 text-xs gap-1.5 mt-2 border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/10">
                      <MessageCircle className="w-3.5 h-3.5" /> Talk to {c.title}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {chatNode && <NpcChatModal projectId={projectId} node={chatNode} onClose={() => setChatNode(null)} />}

      {sub === "animation" && (
        <div data-testid="gv2-animation-panel">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Film className="w-4 h-4 text-violet-400" /> Animation & Motion System</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{motion ? "Motion profile set" : "Not designed yet"}</div>
            </div>
            <Button size="sm" disabled={disabled} data-testid="gv2-generate-animation-btn"
              onClick={() => run("anim", `/gv2/projects/${projectId}/animation`, { brief: animBrief }, "Animation Director is designing the motion system…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
              {busy === "anim" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {motion ? "Re-tune" : "Design animation"}
            </Button>
          </div>
          <input value={animBrief} onChange={(e) => setAnimBrief(e.target.value)}
            placeholder="Optional brief — e.g. snappy responsive combat with a wall-run…"
            data-testid="gv2-anim-brief"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
          {!motion ? (
            <Empty>No animation system yet. The Animation Director will author locomotion, combat, traversal and emote clips plus a motion-matching locomotion profile.</Empty>
          ) : (
            <div>
              {motion.spec?.motion_matching && (
                <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-4 mb-3" data-testid="gv2-motion-profile">
                  <div className="text-xs font-bold uppercase tracking-wide text-violet-400 mb-1">Motion Matching</div>
                  <div className="text-sm">{motion.spec.style}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {(motion.spec.motion_matching.gaits || []).map((g, i) => <Badge key={i} variant="outline" className="text-[10px]">{g}</Badge>)}
                    {motion.spec.motion_matching.responsiveness && <Badge variant="outline" className="text-[10px] border-cyan-500/40 text-cyan-400 capitalize">{motion.spec.motion_matching.responsiveness}</Badge>}
                  </div>
                </div>
              )}
              <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.04] p-4 mb-3" data-testid="gv2-mocap-panel">
                <div className="flex items-center gap-1.5 mb-1">
                  <PersonStanding className="w-4 h-4 text-emerald-400" />
                  <span className="font-bold text-sm flex-1">Mocap From Text <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))]">· DeepMotion AI motion capture</span></span>
                  <Badge variant="outline" className="text-[9px]">{mocapClips.length} clip{mocapClips.length === 1 ? "" : "s"}</Badge>
                </div>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">Describe a move — GameView generates an AI mocap clip and retargets it onto your cast.</p>
                <div className="flex gap-2">
                  <input value={mocapPrompt} onChange={(e) => setMocapPrompt(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && mocapPrompt.trim().length > 1 && !disabled) { run("mocap", `/gv2/projects/${projectId}/mocap`, { prompt: mocapPrompt.trim() }, "DeepMotion is capturing your move…"); setMocapPrompt(""); } }}
                    placeholder="e.g. a spinning heel kick, a victory backflip, a weary limp…"
                    data-testid="gv2-mocap-input"
                    className="flex-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                  <Button size="sm" disabled={disabled || mocapPrompt.trim().length < 2} data-testid="gv2-mocap-btn"
                    onClick={() => { run("mocap", `/gv2/projects/${projectId}/mocap`, { prompt: mocapPrompt.trim() }, "DeepMotion is capturing your move…"); setMocapPrompt(""); }}
                    className="rounded-full gap-1.5 bg-gradient-to-r from-emerald-500 to-cyan-500 shrink-0">
                    {busy === "mocap" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PersonStanding className="w-4 h-4" />}
                    Capture
                  </Button>
                </div>
                {mocapClips.length > 0 && (
                  <div className="space-y-1.5 mt-3">
                    {mocapClips.slice().reverse().slice(0, 12).map((mc, i) => (
                      <div key={mc.id || i} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2" data-testid={`gv2-mocap-clip-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm flex-1 truncate">{mc.name}</span>
                          <Badge variant="outline" className="text-[9px] capitalize">{mc.category}</Badge>
                          <Badge variant="outline" className={`text-[9px] ${mc.provider === "deepmotion" && mc.status !== "ERROR" ? "border-emerald-500/50 text-emerald-400" : "border-amber-500/40 text-amber-400"}`}>
                            {mc.provider === "deepmotion" && mc.status !== "ERROR" ? "DeepMotion" : "AI-authored"}
                          </Badge>
                        </div>
                        {mc.description && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{mc.description}</div>}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                          <span>{mc.duration}s{mc.loop ? " · loop" : ""}</span>
                          {(mc.retarget || []).length > 0 && <span>· → {mc.retarget.length} cast</span>}
                          {mc.url && <a href={mc.url} target="_blank" rel="noreferrer" className="text-emerald-400 hover:underline inline-flex items-center gap-0.5"><Download className="w-3 h-3" /> animation</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                {clips.map((c) => (
                  <div key={c.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`gv2-anim-card-${c.id}`}>
                    <div className="font-semibold text-sm capitalize flex items-center gap-1.5"><Film className="w-3.5 h-3.5 text-violet-400" /> {c.spec?.category}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(c.spec?.clips || []).slice(0, 10).map((clip, i) => <span key={i} className="text-[10px] bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">{String(clip)}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════ Phase 4 — Audio & Cinematics ══════════════ */
const VFX_STYLE = {
  impact: "border-rose-500/40 text-rose-400", ability: "border-cyan-500/40 text-cyan-400",
  weather: "border-sky-500/40 text-sky-400", environment: "border-emerald-500/40 text-emerald-400",
  ui: "border-violet-500/40 text-violet-400",
};

export function AudioFxView({ projectId, nodes, job, reload }) {
  const [sub, setSub] = useState("audio");
  const [aBrief, setABrief] = useState("");
  const [cBrief, setCBrief] = useState("");
  const [vBrief, setVBrief] = useState("");
  const [busy, run] = useStageRun(job, reload);
  const disabled = !!job || !!busy;

  const music = nodes.find((n) => n.type === "MUSIC");
  const sound = nodes.find((n) => n.type === "AUDIO" && n.title === "Sound Design");
  const gameId = nodes.find((n) => n.type === "GAME")?.id;
  const cinematics = nodes.filter((n) => n.type === "CINEMATIC" && n.parent === gameId);
  const vfx = nodes.filter((n) => n.type === "VFX");
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const barkClips = sound?.spec?.bark_clips || [];
  const barkUrl = (t) => { const c = barkClips.find((x) => x.text === t); return c ? BACKEND + c.url : null; };
  const playClip = (url) => { try { new Audio(url).play().catch(() => {}); } catch (e) { /* noop */ } };

  return (
    <div className="max-w-3xl" data-testid="gv2-audiofx-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Phase 4 — the AI workforce composes the adaptive soundtrack & sound design, stages cinematics, and designs the
        signature VFX. ElevenLabs is the premium voice/sound provider; the Web Runtime plays it back today.
      </p>
      <SubTabs sub={sub} setSub={setSub} subs={[
        { key: "audio", label: "Audio & Music", icon: Music },
        { key: "cinematics", label: "Cinematics", icon: Clapperboard },
        { key: "vfx", label: "VFX", icon: Zap },
      ]} />

      {sub === "audio" && (
        <div data-testid="gv2-audio-panel">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Music className="w-4 h-4 text-cyan-400" /> Audio & Music</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{music ? "Adaptive soundtrack set" : "Not designed yet"}</div>
            </div>
            <Button size="sm" disabled={disabled} data-testid="gv2-generate-audio-btn"
              onClick={() => run("audio", `/gv2/projects/${projectId}/audio`, { brief: aBrief }, "Audio Director is composing…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
              {busy === "audio" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {music ? "Recompose" : "Design audio"}
            </Button>
          </div>
          <input value={aBrief} onChange={(e) => setABrief(e.target.value)}
            placeholder="Optional brief — e.g. tense synth score with orchestral boss theme…"
            data-testid="gv2-audio-brief"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
          {!music ? (
            <Empty>No audio yet. The Audio Director will design an adaptive music system (exploration → combat → boss) plus ambience, SFX and voice barks.</Empty>
          ) : (
            <div className="space-y-3">
              <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-4" data-testid="gv2-music-card">
                <div className="text-xs font-bold uppercase tracking-wide text-violet-400 mb-1">Adaptive Music</div>
                <div className="text-sm">{music.spec?.style}</div>
                {music.spec?.instrumentation && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{music.spec.instrumentation}</div>}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(music.spec?.layers || []).map((l, i) => <Badge key={i} variant="outline" className="text-[10px]">{l.when || l.name}: {l.name}</Badge>)}
                </div>
                {music.spec?.transitions && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">↳ {music.spec.transitions}</div>}
              </div>
              {sound && (
                <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gv2-sound-card">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Sound Design</div>
                    {(sound.spec?.voice?.barks || []).length > 0 && (
                      <Button size="sm" variant="outline" disabled={disabled} data-testid="gv2-voice-barks-btn"
                        onClick={() => run("barks", `/gv2/projects/${projectId}/barks`, {}, "ElevenLabs is voicing your barks…")}
                        className="rounded-full gap-1.5 h-7 text-xs">
                        {busy === "barks" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
                        {barkClips.length ? "Re-voice barks" : "Voice barks"}
                      </Button>
                    )}
                  </div>
                  {(sound.spec?.ambient || []).length > 0 && <div className="text-xs mb-1"><span className="text-[hsl(var(--muted-foreground))]">Ambience:</span> {sound.spec.ambient.join(", ")}</div>}
                  {(sound.spec?.sfx || []).length > 0 && <div className="text-xs mb-1"><span className="text-[hsl(var(--muted-foreground))]">SFX:</span> {sound.spec.sfx.join(", ")}</div>}
                  {(sound.spec?.voice?.barks || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {sound.spec.voice.barks.slice(0, 8).map((b, i) => {
                        const url = barkUrl(String(b));
                        return url ? (
                          <button key={i} onClick={() => playClip(url)} data-testid={`gv2-bark-play-${i}`}
                            className="text-[10px] rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 flex items-center gap-1 hover:bg-emerald-500/20 transition-colors">
                            <Play className="w-2.5 h-2.5" fill="currentColor" /> {String(b)}
                          </button>
                        ) : (
                          <span key={i} className="text-[10px] bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">🎙 {String(b)}</span>
                        );
                      })}
                    </div>
                  )}
                  {barkClips.length > 0 && <div className="text-[10px] text-emerald-400 mt-1.5">🔊 {barkClips.length} barks voiced — tap to play</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {sub === "cinematics" && (
        <div data-testid="gv2-cinematics-panel">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Clapperboard className="w-4 h-4 text-amber-400" /> Cinematics</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{cinematics.length} cinematic{cinematics.length === 1 ? "" : "s"}</div>
            </div>
            <Button size="sm" disabled={disabled} data-testid="gv2-generate-cinematics-btn"
              onClick={() => run("cine", `/gv2/projects/${projectId}/cinematics`, { brief: cBrief }, "Cinematic Director is staging your cutscenes…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-amber-500 to-violet-600">
              {busy === "cine" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {cinematics.length ? "Add more" : "Design cinematics"}
            </Button>
          </div>
          <input value={cBrief} onChange={(e) => setCBrief(e.target.value)}
            placeholder="Optional brief — e.g. a noir cold-open and a dramatic boss reveal…"
            data-testid="gv2-cine-brief"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
          {cinematics.length === 0 ? (
            <Empty>No cinematics yet. The Cinematic Director will stage an intro, mid-game beats, a boss reveal and an outro — each with camera direction.</Empty>
          ) : (
            <div className="space-y-2">
              {cinematics.map((c) => (
                <div key={c.id} className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2.5" data-testid={`gv2-cine-card-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <Clapperboard className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="font-semibold text-sm truncate flex-1">{c.title}</span>
                    <Badge variant="outline" className="text-[10px] capitalize">{(c.spec?.type || "cutscene").replace(/_/g, " ")}</Badge>
                    <Badge variant="outline" className="text-[10px] capitalize">{c.spec?.duration || "short"}</Badge>
                  </div>
                  {c.spec?.description && <div className="text-xs mt-1">{c.spec.description}</div>}
                  {c.spec?.camera && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">🎥 {c.spec.camera}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {sub === "vfx" && (
        <div data-testid="gv2-vfx-panel">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div>
              <div className="font-semibold flex items-center gap-1.5"><Zap className="w-4 h-4 text-fuchsia-400" /> Visual Effects</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{vfx.length} effect{vfx.length === 1 ? "" : "s"}</div>
            </div>
            <Button size="sm" disabled={disabled} data-testid="gv2-generate-vfx-btn"
              onClick={() => run("vfx", `/gv2/projects/${projectId}/vfx`, { brief: vBrief }, "VFX Artist is designing your effects…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-fuchsia-500 to-violet-600">
              {busy === "vfx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {vfx.length ? "Add more" : "Design VFX"}
            </Button>
          </div>
          <input value={vBrief} onChange={(e) => setVBrief(e.target.value)}
            placeholder="Optional brief — e.g. neon energy trails and sparking impacts…"
            data-testid="gv2-vfx-brief"
            className="w-full rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-4 py-2 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-violet-500/40" />
          {vfx.length === 0 ? (
            <Empty>No VFX yet. The VFX Artist will design impact, ability, weather and environmental effects that give the game punch and polish.</Empty>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {vfx.map((v) => (
                <div key={v.id} className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid={`gv2-vfx-card-${v.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-fuchsia-400 shrink-0" />
                    <span className="font-bold text-sm truncate flex-1">{v.title}</span>
                    <Badge variant="outline" className={`text-[10px] capitalize ${VFX_STYLE[v.spec?.type] || ""}`}>{v.spec?.type || "impact"}</Badge>
                  </div>
                  {v.spec?.description && <p className="text-xs mb-1">{v.spec.description}</p>}
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))]">
                    {v.spec?.trigger && <span>⚡ {v.spec.trigger}</span>}
                    {v.spec?.intensity && <span className="ml-2 capitalize">· {v.spec.intensity}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════ Phase 5 — AI Playtest & Polish ══════════════ */
const SEV_STYLE = {
  high: "border-rose-500/40 text-rose-400 bg-rose-500/5",
  medium: "border-amber-500/40 text-amber-400 bg-amber-500/5",
  low: "border-slate-500/40 text-slate-400 bg-slate-500/5",
};
const VERDICT_META = {
  ready: { label: "Ready to ship", color: "text-emerald-400", ring: "#34d399" },
  almost: { label: "Almost there", color: "text-amber-400", ring: "#fbbf24" },
  "not-yet": { label: "Not yet", color: "text-rose-400", ring: "#fb7185" },
};

export function PlaytestView({ project, projectId, job, reload }) {
  const [busy, run] = useStageRun(job, reload);
  const disabled = !!job || !!busy;
  const report = project.qa_report;
  const meta = VERDICT_META[report?.verdict] || VERDICT_META.almost;
  const pct = report?.readiness ?? 0;
  const hasIssues = (report?.issues || []).length > 0;

  return (
    <div className="max-w-3xl" data-testid="gv2-playtest-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Phase 5 — the QA Agent plays your compiled build, runs launch checks, and critiques gameplay, visuals, audio,
        balance, UX and performance. Auto-Polish then fixes what it finds — every change is an undoable version.
      </p>
      <div className="flex items-center gap-2 mb-4">
        <Button size="sm" disabled={disabled} data-testid="gv2-run-playtest-btn"
          onClick={() => run("playtest", `/gv2/projects/${projectId}/playtest`, {}, "QA Agent is playtesting your build…")}
          className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
          {busy === "playtest" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
          {report ? "Re-run playtest" : "Run AI playtest"}
        </Button>
        <Button size="sm" variant="outline" disabled={disabled || !hasIssues} data-testid="gv2-autopolish-btn"
          onClick={() => run("polish", `/gv2/projects/${projectId}/polish`, {}, "Auto-Polish is fixing the top issues…")}
          className="rounded-full gap-1.5">
          {busy === "polish" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
          Auto-Polish
        </Button>
      </div>

      <VisualQAScan project={project} projectId={projectId} reload={reload} disabled={disabled} />

      {!report ? (
        <Empty>No playtest yet. Run an AI playtest to get a readiness score and a prioritised list of fixes — then one tap of Auto-Polish applies them.</Empty>
      ) : (
        <div>
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-4 flex items-center gap-5" data-testid="gv2-qa-report">
            <div className="relative w-24 h-24 shrink-0">
              <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(var(--secondary))" strokeWidth="10" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={meta.ring} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${(pct / 100) * 264} 264`} />
              </svg>
              <div className="absolute inset-0 grid place-items-center">
                <div className="text-center">
                  <div className="text-2xl font-extrabold leading-none">{pct}%</div>
                  <div className="text-[9px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">ready</div>
                </div>
              </div>
            </div>
            <div className="min-w-0">
              <div className={`text-lg font-bold ${meta.color}`}>{meta.label}</div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{report.summary}</p>
              {report.checks?.length > 0 && (
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">
                  {report.checks.filter((c) => c.status === "PASSED").length}/{report.checks.length} launch checks passed
                </div>
              )}
            </div>
          </div>

          {report.strengths?.length > 0 && (
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4 mb-4">
              <div className="text-xs font-bold uppercase tracking-wide text-emerald-400 mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> What works</div>
              <ul className="space-y-1">
                {report.strengths.map((s, i) => <li key={i} className="text-sm flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" />{s}</li>)}
              </ul>
            </div>
          )}

          {hasIssues && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Issues to fix ({report.issues.length})</div>
              <div className="space-y-2">
                {report.issues.map((it, i) => (
                  <div key={i} className={`rounded-xl border p-3 ${SEV_STYLE[it.severity] || SEV_STYLE.medium}`} data-testid={`gv2-qa-issue-${i}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-[10px] uppercase">{it.severity}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{it.area}</Badge>
                    </div>
                    <div className="text-sm font-medium text-[hsl(var(--foreground))]">{it.finding}</div>
                    {it.fix && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">🔧 {it.fix}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════ Phase 6 — Ship & Export ══════════════ */
const PROV_PILL = {
  READY: "border-emerald-500/40 text-emerald-400", CONNECTED: "border-emerald-500/40 text-emerald-400",
  CONFIGURATION_REQUIRED: "border-amber-500/40 text-amber-400",
  NOT_CONNECTED: "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]",
  ERROR: "border-rose-500/40 text-rose-400",
};
const ProvPill = ({ status }) => (
  <Badge variant="outline" className={`text-[10px] shrink-0 ${PROV_PILL[status] || ""}`}>{String(status || "").replace(/_/g, " ")}</Badge>
);

export function ShipView({ project, projectId, job, reload }) {
  const [busy, run] = useStageRun(job, reload);
  const [publishing, setPublishing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const disabled = !!job || !!busy;
  const pkg = project.package;
  const gameId = project.game_id;
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const doExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await api.get(`/gv2/projects/${projectId}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project.name || "production").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.gameview.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Production bundle exported.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed."); }
    finally { setExporting(false); }
  };

  const doExportHtml = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const { data } = await api.get(`/gv2/projects/${projectId}/export.html`, { responseType: "text" });
      const blob = new Blob([data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(project.name || "game").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.html`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Standalone playable HTML exported.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "HTML export failed."); }
    finally { setExporting(false); }
  };

  const doPublish = async () => {
    if (publishing || !gameId) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/games/${gameId}/publish`, { visibility: "public" });
      if (data.status === "published") {
        toast.success("Published to the Arcade! 🎉");
      } else {
        toast.success(data.message || "Sent for review.");
      }
      await reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Publish failed."); }
    finally { setPublishing(false); }
  };

  return (
    <div className="max-w-3xl" data-testid="gv2-ship-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        Phase 6 — package the compiled build, export a portable production bundle, and publish to the Arcade.
        Unreal cook/package and cloud Pixel Streaming light up when their premium providers connect.
      </p>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button size="sm" disabled={disabled || !gameId} data-testid="gv2-package-btn"
          onClick={() => run("package", `/gv2/projects/${projectId}/package`, {}, "Packaging your release build…")}
          className="rounded-full gap-1.5 bg-gradient-to-r from-cyan-500 to-violet-600">
          {busy === "package" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
          {pkg ? "Re-package" : "Package build"}
        </Button>
        <Button size="sm" variant="outline" disabled={exporting} onClick={doExport} data-testid="gv2-export-btn" className="rounded-full gap-1.5">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export bundle
        </Button>
        <Button size="sm" variant="outline" disabled={exporting || !gameId} onClick={doExportHtml} data-testid="gv2-export-html-btn" className="rounded-full gap-1.5">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Playable HTML
        </Button>
        <Button size="sm" disabled={publishing || !gameId} onClick={doPublish} data-testid="gv2-publish-btn"
          className="rounded-full gap-1.5 bg-gradient-to-r from-emerald-500 to-cyan-600">
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Publish to Arcade
        </Button>
      </div>

      {!pkg ? (
        <Empty>No release package yet. Package the build to compute export formats, platform targets and a shippability gate — then publish to the Arcade or export a portable bundle.</Empty>
      ) : (
        <div className="space-y-4">
          <div className={`rounded-2xl border p-4 flex items-center gap-3 ${pkg.gate_passed ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="gv2-package-gate">
            {pkg.gate_passed ? <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" /> : <AlertTriangle className="w-6 h-6 text-amber-400 shrink-0" />}
            <div className="min-w-0 flex-1">
              <div className="font-bold">{pkg.gate_passed ? "Shippable" : "Not gated yet"}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                {pkg.engine_name} · {pkg.node_count} nodes · {pkg.version_count} versions
                {pkg.readiness != null ? ` · QA readiness ${pkg.readiness}%` : " · run an AI playtest to gate on quality"}
              </div>
            </div>
            {gameId && (
              <a href={`${origin}/app/build/${gameId}`} target="_blank" rel="noreferrer" data-testid="gv2-ship-play"
                className="text-xs text-violet-400 hover:underline flex items-center gap-1 shrink-0"><ExternalLink className="w-3.5 h-3.5" /> Play</a>
            )}
          </div>

          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gv2-export-formats">
            <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Export formats</div>
            <div className="space-y-2">
              {(pkg.formats || []).map((f) => (
                <div key={f.key} className="flex items-center gap-3">
                  <Package className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm">{f.name}</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{f.note}</div>
                  </div>
                  <ProvPill status={f.status} />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gv2-ship-targets">
            <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Platform targets</div>
            <div className="flex flex-wrap gap-2">
              {(pkg.targets || []).map((t, i) => (
                <div key={i} className="flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-2.5 py-1">
                  <span className="text-xs">{t.platform}</span><ProvPill status={t.status} />
                </div>
              ))}
            </div>
            {pkg.pixel_streaming && (
              <div className="flex items-center gap-3 mt-3 pt-3 border-t border-[hsl(var(--border))]" data-testid="gv2-pixel-streaming">
                <Radio className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm">Pixel Streaming (cloud gameplay)</div>
                  <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{pkg.pixel_streaming.note}</div>
                </div>
                <ProvPill status={pkg.pixel_streaming.status} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════ Stage 1 — Engine (Web Runtime vs Unreal) ══════════════ */
export function EngineView({ project, projectId, job, reload, providers = [] }) {
  const [busy, run] = useStageRun(job, reload);
  const [switching, setSwitching] = useState("");
  const [tiers, setTiers] = useState([]);
  const [dl, setDl] = useState(false);
  const [streaming, setStreaming] = useState(null);
  const [streamBusy, setStreamBusy] = useState(false);
  const [streamCfg, setStreamCfg] = useState(null);
  const [streamUrlInput, setStreamUrlInput] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);
  const [bridge, setBridge] = useState(null);
  const disabled = !!job || !!busy;
  const engine = project.engine || "web_runtime";
  const quality = project.dna?.identity?.quality_target || project.quality || "prototype";
  const ue = project.unreal;
  const build = project.unreal_build;
  const prov = (k) => providers.find((p) => p.key === k)?.status || "NOT_CONNECTED";
  const streamReady = prov("pixel_streaming") === "CONNECTED" || prov("gpu_worker") === "CONNECTED";

  useEffect(() => {
    api.get("/gv2/quality-tiers").then(({ data }) => setTiers(data.tiers || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/gv2/projects/${projectId}/stream-url`)
      .then(({ data }) => { setStreamCfg(data); setStreamUrlInput(data.url || ""); })
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    api.get(`/gv2/bridge/status`).then(({ data }) => setBridge(data)).catch(() => {});
  }, [projectId]);

  const saveStreamUrl = async (url) => {
    if (savingUrl) return;
    setSavingUrl(true);
    try {
      const { data } = await api.put(`/gv2/projects/${projectId}/stream-url`, { url });
      setStreamCfg((c) => ({ ...(c || {}), url: data.url, source: data.source }));
      setStreamUrlInput(data.url || "");
      toast.success(data.url ? "Saved — 'Play in cloud' now streams your build." : "Reverted to the starter app.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the stream URL"); }
    finally { setSavingUrl(false); }
  };

  const setEngine = async (e) => {
    if (disabled || switching || e === engine) return;
    setSwitching(e);
    try { await api.post(`/gv2/projects/${projectId}/engine`, { engine: e }); toast.success(`Engine → ${e === "unreal" ? "Unreal" : "Web Runtime"}`); await reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't switch engine"); }
    finally { setSwitching(""); }
  };
  const setTier = async (t) => {
    if (disabled || switching) return;
    setSwitching(`tier${t}`);
    try { await api.post(`/gv2/projects/${projectId}/quality`, { tier: t }); toast.success(`Quality → ${t}`); await reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't set quality"); }
    finally { setSwitching(""); }
  };
  const downloadUE = async () => {
    if (dl) return;
    setDl(true);
    try {
      const { data } = await api.get(`/gv2/projects/${projectId}/unreal/project.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(project.name || "unreal-project").replace(/[^a-z0-9]+/gi, "-")}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Unreal project downloaded.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
    finally { setDl(false); }
  };
  const downloadWorkerKit = async () => {
    try {
      const { data } = await api.get(`/gv2/worker-kit.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = "gameview-unreal-worker.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Worker kit downloaded — run it on your GPU box, then paste UNREAL_WORKER_URL.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Download failed"); }
  };
  const startStream = async () => {
    if (streamBusy) return;    setStreamBusy(true);
    try {
      const { data } = await api.post(`/gv2/projects/${projectId}/stream`, {});
      setStreaming(data.session);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the stream"); }
    finally { setStreamBusy(false); }
  };

  const ENGINES = [
    { key: "web_runtime", name: "GameView Web Runtime", icon: Gamepad2, status: "READY",
      note: "Instant, in-browser (Three.js / Phaser). Plays right now." },
    { key: "unreal", name: "Unreal Engine 5.8 (Studio Pro)", icon: Boxes, status: prov("unreal"),
      note: "AAA production engine. Generates a real .uproject + build manifest now; cook/package/stream via a cloud worker." },
  ];

  return (
    <div className="max-w-3xl" data-testid="gv2-engine-view">
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-3">
        GameView is the AI control layer above the engine. Pick the engine and quality tier — the whole pipeline adapts.
        Unreal generates a real project + build manifest now; connect a cloud worker (UNREAL_WORKER_URL) to cook, package and pixel-stream.
      </p>

      {/* Engine choice */}
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        {ENGINES.map((e) => {
          const Icon = e.icon; const active = engine === e.key;
          return (
            <button key={e.key} onClick={() => setEngine(e.key)} disabled={disabled} data-testid={`gv2-engine-${e.key}`}
              className={`text-left rounded-2xl border p-4 transition-colors ${active ? "border-violet-500 bg-violet-500/10" : "border-[hsl(var(--border))] hover:border-violet-500/40"}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-5 h-5 text-violet-400" />
                <span className="font-bold text-sm flex-1">{e.name}</span>
                {switching === e.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <ProvPill status={e.status} />}
              </div>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">{e.note}</p>
              {active && <div className="text-[10px] font-bold text-violet-400 mt-2 uppercase tracking-wide">● Active engine</div>}
            </button>
          );
        })}
      </div>

      {/* Quality tier */}
      <div className="mb-5">
        <div className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Game Quality Tier</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="gv2-quality-tiers">
          {tiers.map((t) => {
            const active = quality === t.key;
            return (
              <button key={t.key} onClick={() => setTier(t.key)} disabled={disabled} data-testid={`gv2-tier-${t.key}`}
                className={`text-left rounded-xl border p-2.5 transition-colors ${active ? "border-cyan-500 bg-cyan-500/10" : "border-[hsl(var(--border))] hover:border-cyan-500/40"}`}>
                <div className="text-lg leading-none">{t.emoji}</div>
                <div className="text-xs font-bold mt-1">{t.label}</div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5 line-clamp-2">{t.note}</div>
                {switching === `tier${t.key}` && <Loader2 className="w-3 h-3 animate-spin mt-1" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Unreal Studio Pro */}
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4" data-testid="gv2-unreal-panel">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="font-semibold flex items-center gap-1.5"><Boxes className="w-4 h-4 text-violet-400" /> Unreal Studio Pro</div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">Worker</span><ProvPill status={prov("unreal")} />
            <span className="text-[10px] text-[hsl(var(--muted-foreground))] ml-1">Stream</span><ProvPill status={prov("pixel_streaming")} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button size="sm" disabled={disabled} data-testid="gv2-generate-unreal-btn"
            onClick={() => run("unreal", `/gv2/projects/${projectId}/unreal`, {}, "Compiling your Game DNA into an Unreal project…")}
            className="rounded-full gap-1.5 bg-gradient-to-r from-violet-600 to-cyan-500">
            {busy === "unreal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Boxes className="w-4 h-4" />}
            {ue ? "Regenerate Unreal project" : "Generate Unreal project"}
          </Button>
          {ue && (
            <Button size="sm" disabled={disabled} data-testid="gv2-cook-package-btn"
              onClick={() => run("unreal_build", `/gv2/projects/${projectId}/unreal/build`, {}, "Cooking & packaging your Unreal build on the cloud worker…")}
              className="rounded-full gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500">
              {busy === "unreal_build" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
              {build ? "Re-cook & package" : "Cook & package"}
            </Button>
          )}
          {ue && (
            <Button size="sm" variant="outline" disabled={dl} onClick={downloadUE} data-testid="gv2-download-unreal-btn" className="rounded-full gap-1.5">
              {dl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download .uproject (.zip)
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={streamBusy || !!job} onClick={startStream} data-testid="gv2-pixel-stream-btn"
            title={streamReady ? "Play the Unreal build streamed live from a cloud GPU" : "Preview cloud play (connect PIXEL_STREAMING_URL for a real stream)"}
            className={`rounded-full gap-1.5 ${streamReady ? "border-emerald-500/50 text-emerald-400" : ""}`}>
            {streamBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />} Play in cloud
          </Button>
        </div>

        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))]/40 p-3 mb-3" data-testid="gv2-stream-source">
          <div className="flex items-center gap-2 mb-1">
            <Radio className="w-4 h-4 text-emerald-400" />
            <span className="font-bold text-sm flex-1">Stream source</span>
            <Badge variant="outline" className={`text-[10px] ${streamCfg?.source === "your_build" ? "border-emerald-500/50 text-emerald-400" : "border-amber-500/40 text-amber-400"}`} data-testid="gv2-stream-source-badge">
              {streamCfg?.source === "your_build" ? "Your build" : streamCfg?.source === "starter" ? "Starter app" : "Not set"}
            </Badge>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">
            {streamCfg?.source === "your_build"
              ? "\u201cPlay in cloud\u201d streams your own cooked Unreal build. This is saved to the game's cinematic_url — the same field Cinematic Mode uses."
              : "\u201cPlay in cloud\u201d currently streams the shared Unreal starter app. Cook & upload this project to Eagle 3D (Unreal worker), or paste your own E3DS app URL below to stream your game."}
          </p>
          <div className="flex gap-2">
            <input value={streamUrlInput} onChange={(e) => setStreamUrlInput(e.target.value)}
              placeholder="https://connector.eagle3dstreaming.com/v5/GameView/<YourApp>/default"
              data-testid="gv2-stream-url-input"
              className="flex-1 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
            <Button size="sm" disabled={savingUrl || streamUrlInput.trim() === (streamCfg?.url || "")} onClick={() => saveStreamUrl(streamUrlInput.trim())}
              data-testid="gv2-stream-url-save" className="rounded-full h-8 px-3 text-xs gap-1.5 bg-gradient-to-r from-emerald-500 to-cyan-500 shrink-0">
              {savingUrl ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
            </Button>
            {streamCfg?.url && (
              <Button size="sm" variant="outline" disabled={savingUrl} onClick={() => saveStreamUrl("")}
                data-testid="gv2-stream-url-clear" className="rounded-full h-8 px-3 text-xs shrink-0">Clear</Button>
            )}
          </div>
          <button onClick={downloadWorkerKit} data-testid="gv2-worker-kit-btn"
            className="text-[10px] text-cyan-400 hover:text-cyan-300 inline-flex items-center gap-1 mt-2">
            <Download className="w-3 h-3" /> No worker yet? Download the Unreal worker kit (auto-cooks & uploads to Eagle 3D)
          </button>
          {bridge && (
            <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] flex items-center gap-2 flex-wrap" data-testid="gv2-bridge-status">
              <span className="text-[10px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Secure bridge</span>
              <Badge variant="outline" className={`text-[10px] ${
                !bridge.worker_configured ? "border-sky-500/40 text-sky-400"
                : bridge.secured ? "border-emerald-500/50 text-emerald-400"
                : "border-amber-500/50 text-amber-400"}`} data-testid="gv2-bridge-badge">
                {!bridge.worker_configured ? "Ready" : bridge.secured ? "🔒 Secured" : "⚠ Unsecured"}
              </Badge>
              <span className="text-[10px] text-[hsl(var(--muted-foreground))]">
                {bridge.version} · {!bridge.worker_configured
                  ? "HMAC-signed, capability-negotiated protocol — set UNREAL_WORKER_URL + UNREAL_WORKER_SECRET to connect a worker."
                  : bridge.secured
                    ? `Worker signed${bridge.worker_online ? " · online" : ""}. Only whitelisted, signed, replay-protected ops accepted.`
                    : "Worker wired but requests are UNSIGNED — set UNREAL_WORKER_SECRET on both sides."}
              </span>
            </div>
          )}
        </div>

        {!ue ? (
          <Empty>No Unreal project generated yet. GameView will compile your Game DNA + Production Graph into a real Unreal {"\u2009"}5.8 project (.uproject, Content/GameView structure) and a DNA→Unreal build manifest a cloud worker can cook & package.</Empty>
        ) : (
          <div className="rounded-xl border border-violet-500/25 bg-violet-500/5 p-3" data-testid="gv2-unreal-summary">
            <div className="flex items-center gap-2 mb-2">
              <Cpu className="w-4 h-4 text-violet-400" />
              <span className="font-bold text-sm flex-1 truncate">{ue.summary?.project_name}</span>
              <Badge variant="outline" className="text-[10px] capitalize">{ue.summary?.tier}</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center mb-2">
              {[["Levels", ue.summary?.levels], ["Enemies", ue.summary?.enemies], ["PCG regions", ue.summary?.pcg_regions],
                ["Characters", ue.summary?.characters], ["Cinematics", ue.summary?.cinematics],
                ["Motion Match", ue.summary?.motion_matching ? "On" : "Off"]].map(([l, v], i) => (
                <div key={i} className="rounded-lg bg-[hsl(var(--background))]/50 py-1.5">
                  <div className="text-sm font-bold">{v}</div><div className="text-[9px] text-[hsl(var(--muted-foreground))] uppercase">{l}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              {(ue.summary?.plugins || []).slice(0, 12).map((p, i) => <span key={i} className="text-[10px] bg-[hsl(var(--muted))]/50 rounded px-1.5 py-0.5">{p}</span>)}
            </div>
            {ue.dispatch?.message && <div className="text-[11px] text-[hsl(var(--muted-foreground))]">🛰️ {ue.dispatch.message}</div>}
          </div>
        )}

        {build && (
          <div className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/5 p-3" data-testid="gv2-unreal-build">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-amber-400" />
              <span className="font-bold text-sm flex-1">Cooked build</span>
              <Badge variant="outline" className={`text-[10px] ${build.status === "ERROR" ? "border-rose-500/50 text-rose-400" : "border-emerald-500/50 text-emerald-400"}`}>
                {build.status}{build.mode === "simulated" ? " · simulated" : build.mode === "worker" ? " · cloud" : ""}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center mb-2">
              {[["Total size", `${Math.round(build.stats?.total_mb || 0)} MB`], ["Shaders", build.stats?.shaders],
                ["Cook time", `${build.stats?.cook_seconds || 0}s`]].map(([l, v], i) => (
                <div key={i} className="rounded-lg bg-[hsl(var(--background))]/50 py-1.5">
                  <div className="text-sm font-bold">{v}</div><div className="text-[9px] text-[hsl(var(--muted-foreground))] uppercase">{l}</div>
                </div>
              ))}
            </div>
            <div className="space-y-1 mb-2">
              {(build.artifacts || []).map((a, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] bg-[hsl(var(--background))]/40 rounded-lg px-2 py-1" data-testid={`gv2-build-artifact-${i}`}>
                  <HardDrive className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                  <span className="font-semibold">{a.platform}</span>
                  <span className="text-[hsl(var(--muted-foreground))]">{a.configuration}</span>
                  {a.pixel_streaming && <Badge variant="outline" className="text-[9px] border-emerald-500/40 text-emerald-400">pixel-stream</Badge>}
                  <span className="ml-auto tabular-nums">{Math.round(a.size_mb)} MB</span>
                </div>
              ))}
            </div>
            {(build.logs || []).length > 0 && (
              <details className="group" data-testid="gv2-build-logs">
                <summary className="flex items-center gap-1.5 cursor-pointer text-[11px] text-[hsl(var(--muted-foreground))] select-none">
                  <Terminal className="w-3.5 h-3.5" /> Build log ({build.logs.length} lines)
                </summary>
                <pre className="mt-1.5 text-[10px] leading-relaxed bg-black/40 rounded-lg p-2 overflow-x-auto text-emerald-300/90 font-mono whitespace-pre-wrap">{build.logs.join("\n")}</pre>
              </details>
            )}
            {build.e3ds_app && (
              <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] px-2.5 py-1.5 mt-2 text-[11px]" data-testid="gv2-build-e3ds-app">
                <span className="text-emerald-400 font-semibold">Eagle 3D app:</span> <span className="font-mono">{build.e3ds_app}</span>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">
                  {build.stream_url ? "Uploaded — 'Play in cloud' streams this build." : "The worker uploads each build under this app name, then Stream My Game plays it automatically."}
                </div>
              </div>
            )}
            {build.message && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">📦 {build.message}</div>}
          </div>
        )}
      </div>

      {streaming && <PixelStreamModal session={streaming} projectName={project.name} onClose={() => setStreaming(null)} />}
    </div>
  );
}

/* Cloud pixel-streaming player — real WebRTC iframe when a provider is connected, else a clear
   "connect a provider" notice. Escapes any card clipping via a fixed full-screen portal. */
function PixelStreamModal({ session, projectName, onClose }) {
  const live = session?.status === "CONNECTED" && session?.stream_url;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
      data-testid="gv2-stream-modal" onClick={onClose}>
      <div className="relative w-full max-w-5xl rounded-2xl overflow-hidden border border-violet-500/30 bg-[hsl(var(--card))] shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[hsl(var(--border))] bg-gradient-to-r from-violet-600/20 to-cyan-500/10">
          <Radio className={`w-4 h-4 ${live ? "text-emerald-400 animate-pulse" : "text-amber-400"}`} />
          <span className="font-bold text-sm flex-1 truncate">
            {live ? "Live Unreal cloud stream" : "Cloud stream"} · {projectName || "GameView"}
          </span>
          {live && <Badge variant="outline" className="text-[10px] border-emerald-500/50 text-emerald-400">{session.provider}</Badge>}
          <button onClick={onClose} data-testid="gv2-stream-close" className="p-1 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {live ? (
          <div className="relative bg-black" style={{ aspectRatio: "16 / 9" }}>
            <iframe title="Unreal Pixel Stream" src={session.stream_url} data-testid="gv2-stream-iframe"
              className="absolute inset-0 w-full h-full"
              allow="fullscreen; autoplay; gamepad; camera; microphone; xr-spatial-tracking"
              allowFullScreen />
          </div>
        ) : (
          <div className="p-8 text-center bg-black/40" style={{ aspectRatio: "16 / 9" }}>
            <div className="h-full flex flex-col items-center justify-center gap-3">
              <Radio className="w-10 h-10 text-amber-400" />
              <div className="font-bold">Pixel streaming not connected</div>
              <p className="text-xs text-[hsl(var(--muted-foreground))] max-w-md">{session?.message}</p>
            </div>
          </div>
        )}
        {live && (
          <div className="px-4 py-2 text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2">
            <Maximize2 className="w-3.5 h-3.5" />
            {session.message}
          </div>
        )}
      </div>
    </div>
  );
}
